import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers";
import { setIn, useFormik } from "formik";
import * as Yup from "yup";
import PropTypes from "prop-types";
import dayjs from "dayjs";
import { useMaterialUIController } from "context";
import { Edit, PhotoCamera } from "@mui/icons-material";
import EmployeeAvatar from "components/EmployeeAvatar";
import ConfirmationDialog from "components/ConfirmationDialog";
import { useTranslation } from "react-i18next";
import { getJobGroupsByJobId } from "services/api";
import { fieldStyles } from "services/utils";
import MDButton from "components/MDButton";
import { useNavigate } from "react-router-dom";

const AddLAndDevDialog = ({
  open,
  onClose,
  onSave,
  empId,
  empCompGaps,
  empTrainingOptions,
  trainingCompMap,
  jobLvls,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const formik = useFormik({
    initialValues: {
      competencyId: "",
      trainingId: "",
      startDate: "",
      endDate: "",
      levelId: "",
    },
    onSubmit: async (values) => {
      setLoading(true);

      // Pass Formik values directly to parent
      const savedSuccessfully = await onSave(empId, values); // parent returns true/false

      if (savedSuccessfully) {
        formik.resetForm();
        onClose(); // only close if save succeeded
      }

      setLoading(false);
    },
  });

  const filteredTrainings = useMemo(() => {
    return empTrainingOptions.filter(
      (t) => String(trainingCompMap?.[t.id]) === String(formik.values.competencyId)
    );
  }, [formik.values.competencyId, empTrainingOptions]);

  const filteredLevels = useMemo(() => {
    return jobLvls.filter((lvl) => String(lvl.competencyId) === String(formik.values.competencyId));
  }, [formik.values.competencyId, jobLvls]);

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("user.trainings.addTraining")}</DialogTitle>

      <DialogContent>
        <form onSubmit={formik.handleSubmit}>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            {/* Competency */}
            <TextField
              select
              label={t("user.trainings.competency")}
              value={formik.values.competencyId}
              onChange={(e) => {
                formik.setFieldValue("competencyId", e.target.value);
                formik.setFieldValue("trainingId", "");
                formik.setFieldValue("levelId", "");
              }}
              sx={fieldStyles}
              fullWidth
              size="small"
            >
              {Object.entries(empCompGaps).map(([id, comp]) => (
                <MenuItem key={id} value={id}>
                  {comp.competencyName}
                </MenuItem>
              ))}
            </TextField>

            {/* Training */}
            <TextField
              select
              label={t("user.trainings.training")}
              value={formik.values.trainingId}
              onChange={(e) => {
                formik.setFieldValue("trainingId", e.target.value);
                formik.setFieldValue("levelId", ""); // reset level when training changes
              }}
              sx={fieldStyles}
              fullWidth
              size="small"
              disabled={!formik.values.competencyId}
            >
              {filteredTrainings.map((training) => (
                <MenuItem key={training.id} value={training.id}>
                  {training.name}
                </MenuItem>
              ))}
            </TextField>

            {/* Start Date */}
            <TextField
              type="date"
              label={t("user.trainings.startDate")}
              InputLabelProps={{ shrink: true }}
              value={formik.values.startDate}
              onChange={(e) => formik.setFieldValue("startDate", e.target.value)}
              sx={fieldStyles}
              fullWidth
              size="small"
            />

            {/* End Date */}
            <TextField
              type="date"
              label={t("user.trainings.endDate")}
              InputLabelProps={{ shrink: true }}
              value={formik.values.endDate}
              onChange={(e) => formik.setFieldValue("endDate", e.target.value)}
              sx={fieldStyles}
              fullWidth
              size="small"
            />

            {/* Level */}
            <TextField
              select
              label={t("user.trainings.expectedOutcome")}
              value={formik.values.levelId}
              onChange={(e) => formik.setFieldValue("levelId", e.target.value)}
              sx={fieldStyles}
              fullWidth
              size="small"
              disabled={!formik.values.competencyId}
            >
              {filteredLevels.map((lvl) => (
                <MenuItem key={lvl.id} value={lvl.id}>
                  {`${lvl.name} (${lvl.value})`}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Actions */}
          {loading ? (
            <Box display="flex" justifyContent="flex-end" p={1}>
              <CircularProgress size={20} sx={{ color: "black" }} />
            </Box>
          ) : (
            <DialogActions sx={{ mt: 2 }}>
              {/* Cancel Button */}
              <Button onClick={handleClose} color="secondary">
                {t("admin.employees.employeeDialog.buttons.cancel")}
              </Button>

              {/* Save Button */}
              <Button
                onClick={formik.handleSubmit}
                color="primary"
                sx={{
                  "&:hover": { backgroundColor: "blue", color: "#fff" },
                }}
              >
                {t("user.trainings.add")}
              </Button>
            </DialogActions>
          )}
        </form>
      </DialogContent>
    </Dialog>
  );
};

AddLAndDevDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  empId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,

  // Competencies (object: { compId: { competencyName, ... } })
  empCompGaps: PropTypes.object.isRequired,

  // Trainings list
  empTrainingOptions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,

  // Map trainingId -> competencyId
  trainingCompMap: PropTypes.object.isRequired,

  // Levels list
  jobLvls: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      competencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  setDraftRows: PropTypes.func.isRequired,
};

export default AddLAndDevDialog;
